import React, { FC, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chip } from '@material-ui/core'
import ChipInput from 'material-ui-chip-input'
import { useTranslation } from 'react-i18next'
import KeywordChipList from './KeywordChipList'
import { hiraToKana, kanaToHira } from '../../../utils/string'
import { ReactSVG } from 'react-svg'

interface Props {
  placeHolder: string
  category: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  service: {
    modalName: string
    options: string[] | undefined
    keywords: string[]
    selectLimit: number
    displayLimit: number
    setKeywords: (keyword: string[]) => void
  }
  resetKeywords: () => void
}

interface Keyword {
  type: string
  word: string
  value: string
  checked: boolean
  displayed: boolean
}

const CategoryAndAppKeywordInput: FC<Props> = (props) => {
  const {
    placeHolder,
    category,
    service,
    resetKeywords,
  } = props

  const SELECT_LIMIT = 10
  const KEYWORD_DISPLAY_LIMIT = service.displayLimit
  const { t } = useTranslation()

  const [keywordList, setKeywordList] = useState<Keyword[]>([])
  const [keywordErrorMessage, setKeywordErrorMessage] = useState('')
  const [isKeywordDisplay, setIsKeywordDisplay] = useState(false)
  const [keywordHintMessage, setKeywordHintMessage] = useState('')
  const [tmpKeyword, setTmpKeyword] = useState<string>('')

  const [selectedKeywordList, setSelectedKeywordList] = useState<string[]>([])

  const checkKeywordLimit = () => {
    if ((service.keywords.length + category.keywords.length + 1) === SELECT_LIMIT) {
      setIsKeywordDisplay(false)
      setKeywordHintMessage(t('search.index.form.limit_category_and_service_name', { num: SELECT_LIMIT }))
    } else if ((service.keywords.length + category.keywords.length + 1) >= SELECT_LIMIT) {
      setIsKeywordDisplay(false)
      setKeywordHintMessage(t('search.index.form.limit_category_and_service_name', { num: SELECT_LIMIT }))
      return true
    }
    return false
  }

  const synchronizeKeywordListChip = (keyword: string | number, deleteMode = false, clearMode = false) => {
    const updateList = keywordList.map((value) => {
      if (value.value === keyword) {
        return { ...value, checked: !deleteMode }
      }
      if (clearMode) {
        return { ...value, displayed: true }
      }
      return value
    })
    setKeywordList(updateList)
  }

  const addServiceKeyword = (keyword: string, clearMode = false) => {
    setKeywordErrorMessage('')
    if (checkKeywordLimit()) {
      return
    }
    service.setKeywords([...service.keywords, keyword])
    synchronizeKeywordListChip(keyword, false, clearMode)
    setTmpKeyword('')
  }

  const addCategoryKeyword = (keyword: string) => {
    setKeywordErrorMessage('')
    if (checkKeywordLimit()) {
      return
    }
    category.setKeywords([...category.keywords, keyword])
    synchronizeKeywordListChip(keyword)
    setTmpKeyword('')
  }

  const resetKeywordList = () => {
    const updateList = keywordList.map((value, index: number) => (
      { ...value, checked: selectedKeywordList.includes(value.word), displayed: (index < KEYWORD_DISPLAY_LIMIT) }))
    setKeywordList(updateList)
  }

  const resetKeywordChip = () => {
    const updateList = keywordList.map((value) => ({ ...value, checked: false }))
    setKeywordList(updateList)
  }

  const closeKeywordModal = () => {
    setTmpKeyword('')
    setIsKeywordDisplay(false)
    resetKeywordList()
  }

  const deleteKeyword = (keyword: string) => {
    if (service.keywords.includes(keyword)) {
      service.setKeywords(service.keywords.filter((val) => val !== keyword))
    } else {
      category.setKeywords(category.keywords.filter((val) => t(`setting.personal.custom_alert.category_name.${val}`) !== keyword))
    }
    synchronizeKeywordListChip(keyword, true)
    setKeywordHintMessage('')
  }

  const keywordSelect = (word: string) => {
    let count = 0
    const lowerCasedWord = word.toLowerCase()
    const hiragana = kanaToHira(word)
    const katakana = hiraToKana(word)
    const updateList = keywordList.map((item) => {
      if (
        item.type === "category"
        || (
          (item.word.toLowerCase().indexOf(lowerCasedWord) > -1 || item.word.indexOf(hiragana) > -1 || item.word.indexOf(katakana) > -1)
          && count < KEYWORD_DISPLAY_LIMIT
        )
      ) {
        count += 1
        return { ...item, displayed: true }
      }
      return { ...item, displayed: false }
    })
    setKeywordList(updateList)
    setTmpKeyword(word)
  }

  const keywordSuggest = (e: React.FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value.trim()
    if (inputValue === "") {
      resetKeywordList()
      setTmpKeyword('')
      return
    }
    setIsKeywordDisplay(true)
    keywordSelect(inputValue)
  }

  const openKeywordModal = () => {
    setIsKeywordDisplay(true)
    resetKeywordList()
  }

  const resetKeyword = () => {
    resetKeywords()
    resetKeywordChip()
    setKeywordHintMessage('')
  }

  const choiceFirstChip = (keyCode: number) => {
    const tmpKeywordList = keywordList.filter((keyword) => keyword.displayed && !keyword.checked && keyword.type === "service")
    if (keyCode !== 13 || !tmpKeywordList.length || !tmpKeyword) return
    addServiceKeyword(tmpKeywordList[0].word, true)
  }

  useEffect(() => {
    setSelectedKeywordList([...service.keywords, ...category.keywords.map((val) => t(`setting.personal.custom_alert.category_name.${val}`))])
  }, [service.keywords, category.keywords])

  useEffect(() => {
    const service_res = service.options
      ? service.options.map((option: string) => ({ type: "service", word: option, value: option, checked: false, displayed: true }))
      : []
    const category_res = category.options
      ? category.options.map((option: string) => ({
        type: "category",
        word: t(`setting.personal.custom_alert.category_name.${option}`),
        value: option,
        checked: false,
        displayed: true,
      }))
      : []
    const res = [...service_res, ...category_res]
    setKeywordList(res)
  }, [])

  return (
    <div>
      <div className="input_field">
        <ReactSVG
          src="/static/images/icon/icon-search.svg"
          className="icon"
        />
        <ChipInput
          id="keywords"
          value={selectedKeywordList}
          placeholder={placeHolder}
          onUpdateInput={keywordSuggest}
          onClick={openKeywordModal}
          newChipKeyCodes={[]}
          newChipKeys={[""]}
          onFocus={openKeywordModal}
          onKeyDown={(key) => choiceFirstChip(key.keyCode)}
          inputValue={tmpKeyword}
          chipRenderer={({ value }, key) => (
            <Chip
              className="chip no_trans"
              key={key}
              label={value}
              variant="outlined"
              onDelete={() => deleteKeyword(value)}
              deleteIcon={<div>×</div>}
            />
          )}
        />
        {
          selectedKeywordList.length !== 0 && (
            <button type="button" className="reset" onClick={() => resetKeyword()}>
              ×
            </button>
          )
        }
      </div>
      <div>
        {
          isKeywordDisplay && (
            <>
              <div className="suggest_box" role="none" onClick={() => setTmpKeyword('')}>
                <div className="suggest_contents">
                  <KeywordChipList
                    modalName={category.modalName}
                    keywordList={keywordList.filter((val) => val.type === "category")}
                    addKeyword={addCategoryKeyword}
                  />
                  <KeywordChipList
                    modalName={service.modalName}
                    keywordList={keywordList.filter((val) => val.type === "service")}
                    addKeyword={addServiceKeyword}
                  />
                </div>
                <button type="button" className="closebox" onClick={closeKeywordModal}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              </div>
              <div className="search_screen" role="presentation" onClick={closeKeywordModal} />
            </>
          )
        }
      </div>
      {
        keywordErrorMessage && (
          <div className="error_field">
            {keywordErrorMessage}
          </div>
        )
      }
      {
        keywordHintMessage && (
          <div className="hint_field">
            {keywordHintMessage}
          </div>
        )
      }
    </div>
  )
}

export default CategoryAndAppKeywordInput
